/**
 * @nanokart/tryon-studio-sdk
 * useProviderCapabilities hook - Derive UI visibility from provider
 */

import { useMemo, useContext } from 'react';
import { TryOnConfigContext } from '../TryOnProvider';
import { deriveUIVisibility, UIVisibility, CatalogProvider } from '../providers/types';

export interface UseProviderCapabilitiesReturn extends UIVisibility {
  /** Provider supports filtering */
  supportsFiltering: boolean;
  /** Provider supports pagination */
  supportsPagination: boolean;
  /** Provider supports search */
  supportsSearch: boolean;
}

/**
 * Hook that determines what UI features should be shown
 * based on provider capabilities + mode + items
 * 
 * @example
 * ```tsx
 * const { showSearch, showFilters, forceSingleItemView } = useProviderCapabilities();
 * 
 * return (
 *   <div>
 *     {showSearch && <SearchBar />}
 *     {showFilters && <FilterPanel />}
 *     {forceSingleItemView ? <SingleItemView /> : <InventoryCarousel />}
 *   </div>
 * );
 * ```
 */
export function useProviderCapabilities(): UseProviderCapabilitiesReturn {
  const configContext = useContext(TryOnConfigContext);
  
  if (!configContext) {
    throw new Error('useProviderCapabilities must be used within TryOnProvider');
  }
  
  const { catalogProvider, config } = configContext;

  const uiVisibility = useMemo(() => {
    return deriveUIVisibility(catalogProvider, {
      singleItemMode: config.singleItemMode,
      items: config.items,
      mode: config.mode,
    });
  }, [catalogProvider, config.singleItemMode, config.items, config.mode]);

  return {
    ...uiVisibility,
    supportsFiltering: catalogProvider.capabilities.supportsFiltering,
    supportsPagination: catalogProvider.capabilities.supportsPagination,
    supportsSearch: catalogProvider.capabilities.supportsSearch,
  };
}
